"use client";

import React, {
  useContext,
  useCallback,
  createContext,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { usePathname } from "next/navigation";
import { debounce } from "lodash";
import { useUser } from "@clerk/nextjs";
import { useMediaQuery } from "react-responsive";

import { useGlobalData } from "./globalContext";
import { shouldLoadMore } from "@/lib/client/utils";
import useSavedFilters from "@/hooks/useSavedFilters";
import { useItemsStore } from "@/stores/itemsStore";
import { useFiltersStore } from "@/stores/filtersStore";
import { useCollectionsStore } from "@/stores/collectionsStore";
import { useUiStore } from "@/stores/uiStore";
import { defaultFilters } from "@/lib/shared/constants";

type SearchContextType = {
  // Items state
  items: SearchResultItem[];
  loading: boolean;
  noResults: boolean;
  loadOnScroll: (e: React.SyntheticEvent) => void;
  /** Trigger loading more items (used by virtualized grid instead of scroll events) */
  loadMore: () => boolean | Promise<boolean>;

  // Stats from search results
  itemTotal: number;
  itemAvgPrice: number | undefined;
  itemMinPrice: number;
  itemMaxPrice: number;
  itemLowerQuartile: number | undefined;
  itemMedian: number | undefined;
  itemUpperQuartile: number | undefined;

  // Filters state
  activeFilters: {
    [key: string]: Filter[];
  };
  searchText?: string;
  savedFilters?: DBSavedFilter[];

  // Filter actions
  resetActiveFilters: () => void;
  updateActiveFilter: (dropdownName: string, newSelection: Filter[]) => void;
  removeFilter: (option: Filter, dropdown?: string) => void;
  updateKeywordSearch: (keyword?: string) => void;
  saveFilters: (filterName: string, existingId?: number) => void;
  selectSavedFilter: (filterId?: number, dbId?: boolean) => void;
  handleDeleteSavedFilter: (id?: number) => void;
  setPreloadBrand: (brand: string) => void;
  setPreloadVendor: (vendor: string) => void;
  setPreloadedType: (type: string) => void;
  setPreloadedFilters: () => void;

  // UI state
  filterPaneOpen: boolean;
  setFilterPaneOpen: (value: boolean) => void;
  toggleFavoritesModal: () => void;
};

const SearchContext = createContext<SearchContextType>({
  // Items state
  items: [],
  loading: false,
  noResults: false,
  loadOnScroll: () => {},
  loadMore: () => false,

  // Stats from search results
  itemTotal: 0,
  itemAvgPrice: undefined,
  itemMinPrice: 0,
  itemMaxPrice: Infinity,
  itemLowerQuartile: undefined,
  itemMedian: undefined,
  itemUpperQuartile: undefined,

  // Filters state
  activeFilters: defaultFilters as unknown as { [key: string]: Filter[] },
  searchText: "",
  savedFilters: [],

  // Filter actions
  resetActiveFilters: () => {},
  updateActiveFilter: () => {},
  removeFilter: () => {},
  updateKeywordSearch: () => {},
  saveFilters: () => {},
  selectSavedFilter: () => {},
  handleDeleteSavedFilter: () => {},
  setPreloadBrand: () => {},
  setPreloadVendor: () => {},
  setPreloadedType: () => {},
  setPreloadedFilters: () => {},

  // UI state
  filterPaneOpen: false,
  setFilterPaneOpen: () => {},
  toggleFavoritesModal: () => {},
});

type SearchProviderProps = {
  children: React.ReactNode;
};

const getRouteSavedFilterId = (pathname: string | null) => {
  if (typeof window === "undefined" || pathname !== "/search") {
    return undefined;
  }

  const params = new URLSearchParams(window.location.search);
  const value = params.get("filterid") ?? params.get("preset");
  if (!value) {
    return undefined;
  }

  const id = Number.parseInt(value, 10);
  return Number.isNaN(id) ? undefined : id;
};

const hasFiltersStorageHydrated = () => useFiltersStore.persist?.hasHydrated?.() ?? true;

export function SearchProvider({ children }: SearchProviderProps) {
  const isMobile = useMediaQuery({ query: "(max-width: 1250px)" });
  const abortController = useRef<AbortController>(null);
  const { user, isLoaded: userLoaded } = useUser();
  const { vendors, brands, exchangeRates, defaultTotal } = useGlobalData();
  const defaultTotalRef = useRef(defaultTotal);
  const globalDataRef = useRef({ vendors, brands, exchangeRates });
  const [filtersStorageReady, setFiltersStorageReady] = useState(false);

  globalDataRef.current = { vendors, brands, exchangeRates };

  const pathname = usePathname();
  const routeSavedFilterId = getRouteSavedFilterId(pathname);
  const isSearchPage =
    pathname === "/search" ||
    pathname?.startsWith("/brands/") ||
    pathname?.startsWith("/search/vendor/") ||
    pathname === "/spotlight";

  const isBrandPage = pathname?.startsWith("/brands/");

  const {
    loadSavedFilters,
    savedFilters: savedFiltersFromHook,
    loading: savedFiltersLoading,
    saveNewFilter,
    updateSavedFilter,
    deleteSavedFilter,
  } = useSavedFilters();

  // Get saved filters from store instead of hook for consistency
  const savedFiltersFromStore = useFiltersStore((state) => state.savedFilters);
  const savedFilters =
    savedFiltersFromStore.length > 0 ? savedFiltersFromStore : savedFiltersFromHook;

  // Get state and actions from separate Zustand stores for better re-rendering
  const items = useItemsStore((state) => state.items);
  const loading = useItemsStore((state) => state.loading);
  const noResults = useItemsStore((state) => state.noResults);
  const itemTotal = useItemsStore((state) => state.itemTotal);
  const itemAvgPrice = useItemsStore((state) => state.itemAvgPrice);
  const itemMinPrice = useItemsStore((state) => state.itemMinPrice);
  const itemMaxPrice = useItemsStore((state) => state.itemMaxPrice);
  const hasReachedEnd = useItemsStore((state) => state.hasReachedEnd);
  const shouldRetry = useItemsStore((state) => state.shouldRetry);
  const retryCount = useItemsStore((state) => state.retryCount);
  const nextRetryAt = useItemsStore((state) => state.nextRetryAt);
  const { loadInitialResults: loadInitialResultsStore, loadMoreItems: loadMoreItemsStore } =
    useItemsStore((state) => state.actions);

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const savedFilterId = useFiltersStore((state) => state.savedFilterId);
  const excludeRelists = useFiltersStore((state) => state.excludeRelists);
  const excludeAuctions = useFiltersStore((state) => state.excludeAuctions);
  const daysBack = useFiltersStore((state) => state.daysBack);
  const kurboMode = useFiltersStore((state) => state.kurboMode);
  const activeFiltersKey = useMemo(() => JSON.stringify(activeFilters), [activeFilters]);
  const initialSearchKey = useMemo(
    () =>
      JSON.stringify({
        activeFilters: activeFiltersKey,
        searchText,
        savedFilterId,
        excludeRelists,
        excludeAuctions,
        daysBack,
        kurboMode,
        isBrandPage,
      }),
    [
      activeFiltersKey,
      searchText,
      savedFilterId,
      excludeRelists,
      excludeAuctions,
      daysBack,
      kurboMode,
      isBrandPage,
    ]
  );
  const globalDataReady = Boolean(vendors?.length && brands?.length && exchangeRates);
  const initialSearchParamsRef = useRef({ activeFilters, searchText, savedFilterId, isBrandPage });
  initialSearchParamsRef.current = { activeFilters, searchText, savedFilterId, isBrandPage };
  const {
    resetActiveFilters: resetActiveFiltersStore,
    updateActiveFilter: updateActiveFilterStore,
    removeFilter: removeFilterStore,
    saveFilters: saveFiltersStore,
    selectSavedFilter: selectSavedFilterStore,
    handleDeleteSavedFilter: handleDeleteSavedFilterStore,
    setPreloadBrand: setPreloadBrandStore,
    setPreloadVendor: setPreloadVendorStore,
    setPreloadedType: setPreloadedTypeStore,
    setPreloadedFilters: setPreloadedFiltersStore,
    updateKeywordSearch: updateKeywordSearchStore,
    setSavedFilters,
  } = useFiltersStore((state) => state.actions);

  const { loadCollections } = useCollectionsStore((state) => state.actions);

  const filterPaneOpen = useUiStore((state) => state.filterPaneOpen);
  const userDataLoaded = useUiStore((state) => state.userDataLoaded);
  const {
    setFilterPaneOpen,
    toggleFavoritesModal: toggleFavoritesModalStore,
    setUserDataLoaded,
  } = useUiStore((state) => state.actions);

  // Initialize filter pane state based on page
  useEffect(() => {
    if (isMobile) return;

    setFilterPaneOpen(pathname === "/search");
  }, [isMobile, pathname, setFilterPaneOpen]);

  // Load user data - but skip loadCollections on public collection pages
  const loadUserData = useCallback(() => {
    if (user && !userDataLoaded) {
      loadSavedFilters({ refreshCache: true });

      // Don't auto-load collection items when viewing a public collection page
      const isViewingPublicCollection = pathname.includes("/favorites/public/");
      if (!isViewingPublicCollection) {
        loadCollections();
      }

      setUserDataLoaded(true);
    }
  }, [user, userDataLoaded, pathname, loadSavedFilters, loadCollections, setUserDataLoaded]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    defaultTotalRef.current = defaultTotal;
  }, [defaultTotal]);

  // Around the useEffect that syncs saved filters
  useEffect(() => {
    if (savedFiltersFromHook && savedFiltersFromHook.length > 0) {
      setSavedFilters(savedFiltersFromHook);
    }
  }, [savedFiltersFromHook, setSavedFilters]);

  useEffect(() => {
    if (hasFiltersStorageHydrated()) {
      setFiltersStorageReady(true);
      return;
    }

    const unsubscribe = useFiltersStore.persist?.onFinishHydration?.(() => {
      setFiltersStorageReady(true);
    });

    if (hasFiltersStorageHydrated()) {
      setFiltersStorageReady(true);
    }

    return unsubscribe;
  }, []);

  const routeSavedFilter = routeSavedFilterId
    ? savedFilters.find((filter) => filter.id === routeSavedFilterId)
    : undefined;

  // Deep links (e.g. saved-filter-match push notifications) land on
  // /search?filterid=X, and that param is deliberately left in the URL so a
  // refresh keeps you on the same saved filter. But routeSavedFilterId is
  // re-read from the URL on every render, and client-side actions (switching
  // saved filters, resetting) never touch the URL. Without a once-per-id
  // guard, this effect kept reasserting the URL's filter on every subsequent
  // render, silently reverting the user's own change before it could paint.
  // Tracking which id has already been applied lets a fresh mount (hard
  // refresh, or tapping a new notification with a different id) still apply
  // once, while leaving later manual switches alone.
  const appliedRouteSavedFilterIdRef = useRef<number | undefined>(undefined);

  const routeSavedFilterPending =
    isSearchPage &&
    !!routeSavedFilterId &&
    appliedRouteSavedFilterIdRef.current !== routeSavedFilterId &&
    (!userLoaded ||
      (!!user &&
        ((!routeSavedFilter && savedFiltersLoading && savedFilters.length === 0) ||
          (!!routeSavedFilter && savedFilterId !== routeSavedFilterId))));

  useLayoutEffect(() => {
    if (!isSearchPage || !routeSavedFilterId || !routeSavedFilter) {
      return;
    }

    if (appliedRouteSavedFilterIdRef.current === routeSavedFilterId) {
      return;
    }

    if (savedFilterId !== routeSavedFilterId) {
      selectSavedFilterStore(routeSavedFilterId, true, savedFilters);
    }
    appliedRouteSavedFilterIdRef.current = routeSavedFilterId;
  }, [
    isSearchPage,
    routeSavedFilterId,
    routeSavedFilter,
    savedFilterId,
    selectSavedFilterStore,
    savedFilters,
  ]);

  // Immediately clear items before paint when filters change, so ghost cards
  // appear without waiting for the useEffect (which fires after paint).
  // Also abort any in-flight request here (before paint) to close the race
  // window where a fast previous-filter response could resolve between paint
  // and the useEffect cleanup running.
  const prevFiltersRef = useRef({ activeFiltersKey, searchText, savedFilterId, kurboMode });
  useLayoutEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.activeFiltersKey !== activeFiltersKey ||
      prev.searchText !== searchText ||
      prev.savedFilterId !== savedFilterId ||
      prev.kurboMode !== kurboMode;
    prevFiltersRef.current = { activeFiltersKey, searchText, savedFilterId, kurboMode };

    if (filtersChanged && isSearchPage) {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
      useItemsStore.setState({
        items: [],
        loading: true,
        noResults: false,
      });
    }
  }, [activeFiltersKey, searchText, savedFilterId, kurboMode, isSearchPage]);

  // Load initial results when filters change. The first eligible run fires
  // immediately (page load / navigation); subsequent runs are debounced 300ms
  // so rapid successive filter changes send one request instead of a burst.
  // Client-side aborts don't help against rate limits — the server already
  // received the request — so the debounce is what actually prevents 429s.
  const hasLoadedInitialResultsRef = useRef(false);
  const lastInitialSearchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    // Create a local AbortController for this specific effect invocation.
    // This prevents StrictMode's cleanup→re-run cycle from aborting the
    // second (real) request via a shared ref.
    const controller = new AbortController();
    let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    if (isSearchPage && filtersStorageReady && !routeSavedFilterPending && globalDataReady) {
      // Pagination updates other store state and can cause this provider to
      // render again. Do not restart the initial flow when the logical query
      // is unchanged; that would repeat the expensive stats/count requests.
      if (
        lastInitialSearchKeyRef.current === initialSearchKey &&
        !abortController.current?.signal.aborted
      ) {
        return;
      }

      const {
        vendors: currentVendors,
        brands: currentBrands,
        exchangeRates: currentExchangeRates,
      } = globalDataRef.current;
      const {
        activeFilters: currentActiveFilters,
        searchText: currentSearchText,
        savedFilterId: currentSavedFilterId,
        isBrandPage: currentIsBrandPage,
      } = initialSearchParamsRef.current;

      if (!currentVendors || !currentBrands || !currentExchangeRates) {
        return;
      }

      // Abort any remaining in-flight request not already caught by the
      // layoutEffect (e.g. when vendors/exchangeRates change but filters didn't)
      if (abortController.current) {
        abortController.current.abort();
      }
      abortController.current = controller;

      const load = () => {
        if (controller.signal.aborted) return;
        lastInitialSearchKeyRef.current = initialSearchKey;
        loadInitialResultsStore({
          vendors: currentVendors,
          brands: currentBrands,
          exchangeRates: currentExchangeRates,
          activeFilters: currentActiveFilters,
          searchText: currentSearchText,
          savedFilterId: currentSavedFilterId,
          signal: controller.signal,
          defaultTotal: defaultTotalRef.current ?? undefined,
          skipTotalOnly: currentIsBrandPage,
        });
      };

      if (hasLoadedInitialResultsRef.current) {
        debounceTimeout = setTimeout(load, 300);
      } else {
        hasLoadedInitialResultsRef.current = true;
        load();
      }
    }

    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      controller.abort();
    };
  }, [
    initialSearchKey,
    isSearchPage,
    filtersStorageReady,
    routeSavedFilterPending,
    globalDataReady,
    loadInitialResultsStore,
  ]);

  // Single retry pulse for failed load-more attempts, app-wide. The items
  // store owns the backoff policy (nextRetryAt); this effect just schedules
  // the next attempt to land right after the backoff window opens. If that
  // attempt fails too, retryCount changes and the effect re-arms itself.
  useEffect(() => {
    if (shouldRetry && !loading && vendors && brands && exchangeRates) {
      const delay = Math.max(nextRetryAt - Date.now() + 100, 500);
      const retryTimeout = setTimeout(() => {
        loadMoreItemsStore({
          vendors,
          brands,
          exchangeRates,
          activeFilters,
          searchText,
          isSignedIn: !!user,
          savedFilterId,
        });
      }, delay);

      return () => clearTimeout(retryTimeout);
    }
  }, [
    shouldRetry,
    loading,
    retryCount,
    nextRetryAt,
    loadMoreItemsStore,
    vendors,
    brands,
    exchangeRates,
    activeFilters,
    searchText,
    user,
    savedFilterId,
  ]);

  // Mobile scroll listener for pages using the non-virtualized CardGrid
  // (brand/category pages). On the search page the virtualized grid owns
  // load-more detection — running both produced duplicate triggers.
  const loadOnScrollMobile = useMemo(
    () =>
      debounce(() => {
        if (isSearchPage) return;
        if (window.innerWidth > 1250) return;

        const scrollTop = window.scrollY;
        const clientHeight = window.innerHeight;
        const scrollHeight = document.documentElement.scrollHeight;

        if (
          shouldLoadMore({ scrollTop, scrollHeight, clientHeight, threshold: 0.5 }) &&
          !loading &&
          !hasReachedEnd &&
          vendors &&
          brands &&
          exchangeRates
        ) {
          loadMoreItemsStore({
            vendors,
            brands,
            exchangeRates,
            activeFilters,
            searchText,
            isSignedIn: !!user,
            savedFilterId,
          });
        }
      }, 100),
    [
      isSearchPage,
      loading,
      hasReachedEnd,
      loadMoreItemsStore,
      vendors,
      brands,
      exchangeRates,
      activeFilters,
      searchText,
      user,
      savedFilterId,
    ]
  );

  useEffect(() => {
    window.addEventListener("scroll", loadOnScrollMobile);

    return () => {
      window.removeEventListener("scroll", loadOnScrollMobile);
      loadOnScrollMobile.cancel();
    };
  }, [loadOnScrollMobile]);

  // Wrapper functions that inject dependencies

  const loadOnScroll = useCallback(
    (e: React.SyntheticEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        shouldLoadMore({ scrollTop, scrollHeight, clientHeight }) &&
        !loading &&
        !hasReachedEnd &&
        vendors &&
        brands &&
        exchangeRates
      ) {
        loadMoreItemsStore({
          vendors,
          brands,
          exchangeRates,
          activeFilters,
          searchText,
          isSignedIn: !!user,
          savedFilterId,
        });
      }
    },
    [
      loading,
      hasReachedEnd,
      loadMoreItemsStore,
      vendors,
      brands,
      exchangeRates,
      activeFilters,
      searchText,
      user,
      savedFilterId,
    ]
  );

  // Direct load-more trigger (for virtualized grid — no scroll event needed)
  const loadMore = useCallback(() => {
    if (!loading && !hasReachedEnd && vendors && brands && exchangeRates) {
      return loadMoreItemsStore({
        vendors,
        brands,
        exchangeRates,
        activeFilters,
        searchText,
        isSignedIn: !!user,
        savedFilterId,
      });
    }

    return false;
  }, [
    loading,
    hasReachedEnd,
    loadMoreItemsStore,
    vendors,
    brands,
    exchangeRates,
    activeFilters,
    searchText,
    user,
    savedFilterId,
  ]);

  const resetActiveFilters = useCallback(() => {
    resetActiveFiltersStore();
  }, [resetActiveFiltersStore]);

  const updateActiveFilter = useCallback(
    (dropdownName: string, newSelection: Filter[]) => {
      updateActiveFilterStore(
        dropdownName,
        newSelection,
        user as User | undefined,
        savedFilters,
        updateSavedFilter
      );
    },
    [updateActiveFilterStore, user, savedFilters, updateSavedFilter]
  );

  const removeFilter = useCallback(
    (option: Filter, dropdown?: string) => {
      removeFilterStore(
        option,
        dropdown,
        user as User | undefined,
        savedFilters,
        updateSavedFilter
      );
    },
    [removeFilterStore, user, savedFilters, updateSavedFilter]
  );

  const saveFilters = useCallback(
    (filterName: string, existingId?: number) => {
      saveFiltersStore(filterName, existingId, savedFilters, saveNewFilter, updateSavedFilter);
    },
    [saveFiltersStore, savedFilters, saveNewFilter, updateSavedFilter]
  );

  const selectSavedFilter = useCallback(
    (filterId?: number, dbId = false) => {
      selectSavedFilterStore(filterId, dbId, savedFilters);
    },
    [selectSavedFilterStore, savedFilters]
  );

  const handleDeleteSavedFilter = useCallback(
    (filterId?: number) => {
      handleDeleteSavedFilterStore(filterId, deleteSavedFilter);
    },
    [handleDeleteSavedFilterStore, deleteSavedFilter]
  );

  const setPreloadBrand = useCallback(
    (brand: string) => {
      if (!brands) return;
      setPreloadBrandStore(brand, brands);
    },
    [setPreloadBrandStore, brands]
  );

  const setPreloadVendor = useCallback(
    (vendor: string) => {
      if (!vendors) return;
      setPreloadVendorStore(vendor, vendors);
    },
    [setPreloadVendorStore, vendors]
  );

  const setPreloadedType = useCallback(
    (typeValue: string) => {
      setPreloadedTypeStore(typeValue);
    },
    [setPreloadedTypeStore]
  );

  const setPreloadedFilters = useCallback(() => {
    if (!brands || !vendors) return;
    setPreloadedFiltersStore(brands, vendors);
  }, [setPreloadedFiltersStore, brands, vendors]);

  const updateKeywordSearch = useCallback(
    (keyword?: string) => {
      updateKeywordSearchStore(keyword, user as User | undefined, savedFilters, updateSavedFilter);
    },
    [updateKeywordSearchStore, user, savedFilters, updateSavedFilter]
  );

  const toggleFavoritesModal = useCallback(() => {
    toggleFavoritesModalStore();
  }, [toggleFavoritesModalStore]);

  return (
    <SearchContext.Provider
      value={{
        // Items state
        items,
        loading,
        noResults,
        loadOnScroll,
        loadMore,

        // Stats from search results
        itemTotal,
        itemAvgPrice,
        itemMinPrice,
        itemMaxPrice,
        itemLowerQuartile: undefined,
        itemMedian: undefined,
        itemUpperQuartile: undefined,

        // Filters state
        activeFilters,
        searchText,
        savedFilters,

        // Filter actions
        resetActiveFilters,
        updateActiveFilter,
        removeFilter,
        updateKeywordSearch,
        saveFilters,
        selectSavedFilter,
        handleDeleteSavedFilter,
        setPreloadBrand,
        setPreloadVendor,
        setPreloadedType,
        setPreloadedFilters,

        // UI state
        filterPaneOpen,
        setFilterPaneOpen,
        toggleFavoritesModal,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export function useSearchContext() {
  return useContext(SearchContext);
}
